/**
 * HotelListResult component
 * This component displays a list of hotels with filtering, sorting, and map view capabilities.
 * It handles hotel data fetching, filtering by city/brand/tags, price retrieval, and renders hotel sliders, banners, and membership sections.
 * The logic includes grouping hotels, sorting, and managing UI state for map/list views.
 */

"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import MemberShipSection from "@/components/organisms/hotel-details/MemberShipSection";
import HotelListSlider, {
  IHotelListSliderProps,
  THotelData,
} from "@/components/organisms/hotel-list-slider";
import SearchByCityAndHotel from "../SelectByCityAndHotel";
import {
  HotelBenefitListQuery,
  MemberShipBannerQuery,
  JapanHotel,
  HotelListBannersQuery,
  SetOfTag,
} from "@/types/generated";
import { SearchAvailableRoom } from "../search-avaliable/SearchAvailableRoomMobile";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { SelectOption } from "../search-avaliable/SearchAvailableRoom";
import {
  chunkArray,
  formatDateToYYYYMMDD,
  groupJapanAreas,
  TOKYOID,
} from "@/utils";
import { handleFilterByFilterData } from "./utils";
import { findOption } from "../search-avaliable/findOption";
import PopularSection, { IPopularImage } from "./popular/PopularSection";
import FilterSection from "./filter/FilterSection";

// Lazy load HotelListMap to avoid bundling Google Maps in initial JS
const HotelListMap = dynamic(() => import("./hotel-list-map"), {
  ssr: false,
  loading: () => (
    <div className="h-[600px] w-full animate-pulse rounded-lg bg-gray-200" />
  ),
});

import { HotelListParams } from "@/types/roomsList";
import { getLowestPriceHotels } from "@app/api/tripla/hotel";
import {
  BrandFormatByItemName,
  DEFAULT_ADULTS,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { MapBrandIcon } from "@app/[locale]/tourist-attraction/[city_slug]/[slug]/util";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { useHasHydrated } from "@/hooks/useHasHydrated/useHasHydrated";
import { useHotelCount } from "@/hooks/useHotelCount/useHotelCount";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/atoms/skelton";
import { IBanner } from "./banner/BannerBrand";
import { BannerSection } from "./banner/BannerSection";
import { IFieldSelectOrder } from "@/types/common";

export interface IBrandPageLayoutProps {
  bannerHotelList?: HotelListBannersQuery;
  popularImages: IPopularImage[];
  hotelSliderSections: IHotelListSliderProps[];
  membershipProps: MemberShipBannerQuery;
  benefitList?: HotelBenefitListQuery;
  brandList?: HotelBrandItemNullable[];
  currentCity?: string;
  isHotelListPage?: boolean;
  currentBrand?: HotelBrandItemNullable;
  brandBannerList: IBanner[];
  brandIcons?: MapBrandIcon[];
  listHotelTriplaBackendCode?: string[];
  hotSpringSection?: IHotelListSliderProps;
  hasSlugCity?: boolean;
  isHotelOnSenPage?: boolean;
  currentTag?: SetOfTag;
}
export enum TypeSearch {
  CITY = "city",
  HOTEL = "hotel",
}

export interface SelectOptionValue extends SelectOption {
  type?: string;
  searchNames?: string[];
  citySearchNames?: string[];
}

const HotelListResult: React.FC<IBrandPageLayoutProps> = ({
  bannerHotelList,
  popularImages,
  hotelSliderSections,
  membershipProps,
  brandList,
  benefitList,
  currentCity,
  isHotelListPage,
  currentBrand,
  brandBannerList,
  brandIcons,
  listHotelTriplaBackendCode,
  hotSpringSection,
  hasSlugCity,
  isHotelOnSenPage,
  currentTag,
}) => {
  /**
   * State to store hotels with their minimum price fetched from Tripla API
   */
  const [hotelsWithPrice, setHotelsWithPrice] = useState<
    { triplaCode: number; minPrice: number }[]
  >([]);
  const {
    japanAreas,
    loading,
    allLocaleHotels,
    loadingAllLocaleHotels,
    imageFetched,
  } = useJapanAreasStore();
  /**
   * Memoized list of hotel and city options, grouped and sorted by brand order
   */
  const listHotelAndCityData = useMemo(() => {
    if (!japanAreas?.length) return [];
    // Build a map for fast order lookup
    const orderMap = new Map(
      currentBrand?.hotel_in_brand_top?.map((i: IFieldSelectOrder) => [
        i.documentId,
        i.order,
      ]),
    );
    const groupData = groupJapanAreas(
      japanAreas,
      allLocaleHotels as Record<string, JapanHotel[]> | null | undefined,
      currentCity,
      "都市",
      "ホテル",
      currentBrand,
      hotelSliderSections,
    );
    // Sort options (hotels) inside each group by orderMap
    return groupData.map((group) => {
      const sortedOptions = Array.isArray(group.options)
        ? [...group.options].sort((a, b) => {
            const orderA = orderMap.get(a.value) ?? 9999;
            const orderB = orderMap.get(b.value) ?? 9999;
            return Number(orderA) - Number(orderB);
          })
        : group.options;
      return {
        ...group,
        options: sortedOptions,
      };
    });
  }, [
    japanAreas,
    allLocaleHotels,
    currentCity,
    currentBrand,
    hotelSliderSections,
  ]);
  /**
   * Memoized default city option for filter selection
   */
  const defaultCity = useMemo(() => {
    if (!listHotelAndCityData?.length) return null;
    return findOption(listHotelAndCityData, currentCity);
  }, [listHotelAndCityData, currentCity]);
  /**
   * Memoized default Tokyo option for filter selection
   */
  const defaultTokyo = useMemo(() => {
    if (!listHotelAndCityData?.length) return null;
    return findOption(listHotelAndCityData, TOKYOID);
  }, [listHotelAndCityData]);
  /**
   * Memoized default filter value based on selected city
   */
  const defaultValue = useMemo(() => {
    if (!defaultCity) return null;
    return {
      checkIn: null,
      checkout: null,
      rooms: [],
      hotelOrCity: defaultCity,
    };
  }, [defaultCity]);

  /**
   * State for current filter selection (city/hotel)
   */
  const [filterState, setFilterState] = useState<SearchAvailableRoom | null>(
    () => {
      return isHotelListPage || currentCity ? defaultValue : null;
    },
  );
  useEffect(() => {
    if (defaultValue && (isHotelListPage || currentCity)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilterState(defaultValue);
    }
  }, [defaultValue, isHotelListPage, currentCity]);

  /**
   * State for filtered hotel slider sections based on filter selection
   */
  const [filteredSections, setFilteredSections] = useState<
    IHotelListSliderProps[]
  >(
    isHotelListPage || currentCity || !!currentTag || currentBrand
      ? hotelSliderSections
      : [],
  );
  const [submitted, setSubmitted] = useState(false);
  /**
   * State for hotels visible on the map view
   */
  const [visibleHotels, setVisibleHotels] = useState<THotelData[]>([]);
  /**
   * State for toggling between map and list view
   */
  const [isMapView, setIsMapView] = useState(false);
  /**
   * Callback for filter change event
   */
  const onFilterChange = (f: SearchAvailableRoom | null) => setFilterState(f);
  /**
   * Ref to track if city change is the first one (for UI logic)
   */
  const isFirstCityChange = useRef<boolean>(false);
  const { formData } = useSearchFormStore();
  /**
   * Effect to filter hotel sections based on current filter state and other dependencies
   */
  useEffect(() => {
    handleFilterByFilterData({
      dataFilter: filterState,
      japanAreas,
      hotelSliderSections,
      setFilteredSections,
      currentBrand,
      setVisibleHotels,
      currentCity,
      isFirstCityChange,
    });
  }, [
    filterState,
    hotelSliderSections,
    japanAreas,
    allLocaleHotels,
    loading,
    imageFetched,
    currentBrand,
    currentCity,
  ]);

  /**
   * Calculate the number of unique hotels in the filtered sections
   */
  const seenDocumentIds = new Set<string>();
  const resultLength = filteredSections
    .filter((item) => item.typeGroup === "basic")
    .reduce((acc, cur) => {
      const uniqueItems = (cur.data ?? []).filter((d) => {
        if (!d?.documentId || seenDocumentIds.has(d.documentId)) return false;
        seenDocumentIds.add(d.documentId);
        return true;
      });
      return acc + uniqueItems.length;
    }, 0);

  /**
   * State to track if the submit button was clicked (for price fetching)
   */
  /**
   * Memoized list of hotel codes for price check (combines filtered and hot spring hotels)
   */
  const firstListHotelCheckPrice = useMemo(() => {
    const sectionHotels =
      filteredSections
        ?.flatMap((i) => i.data)
        ?.map((i) => i.triplaBookingCode) || [];
    const hotSpringHotels =
      hotSpringSection?.data?.map((i) => i.triplaBookingCode) || [];
    const filteredHotelsCode = [...sectionHotels, ...hotSpringHotels];
    const hotelCode = filteredHotelsCode?.length
      ? filteredHotelsCode
      : listHotelTriplaBackendCode || [] || [];
    return hotelCode;
  }, [filteredSections, hotSpringSection, listHotelTriplaBackendCode]);
  /**
   * Memoized parameters for Tripla API price fetching
   */
  const params = useMemo<HotelListParams>(() => {
    return {
      brandId: 297,
      rooms: [
        {
          checkin_date: formData?.checkIn
            ? formatDateToYYYYMMDD(formData.checkIn)
            : DEFAULT_CHECKIN_DATE,
          checkout_date: formData?.checkout
            ? formatDateToYYYYMMDD(formData.checkout)
            : DEFAULT_CHECKOUT_DATE,
          adults: formData?.rooms?.[0]
            ? formData?.rooms?.[0]?.adults.toString()
            : DEFAULT_ADULTS,
          children: formData?.rooms?.[0]
            ? formData?.rooms?.[0]?.children.length.toString()
            : "0",
          kids_age_range: [],
        },
      ],
      hotel_codes: firstListHotelCheckPrice,
    } as HotelListParams;
  }, [formData, firstListHotelCheckPrice]);
  /**
   * Effect to fetch hotel prices from Tripla API in batches
   */
  // Helper function to extract hotels with price from API response
  const extractHotelsWithPrice = (
    apiHotels: { lowest_price: number; id: number }[] = [],
  ) => {
    return apiHotels.map(({ lowest_price, id }) => ({
      triplaCode: id,
      minPrice: lowest_price || 0,
    }));
  };

  // Helper function to process results from Promise.allSettled
  interface TriplaHotelResponse {
    data?: { lowest_price: number; id: number }[];
  }

  const processBatchResults = (
    results: PromiseSettledResult<TriplaHotelResponse>[],
  ) => {
    const successfulResults = results
      .filter(
        (result): result is PromiseFulfilledResult<TriplaHotelResponse> =>
          result.status === "fulfilled",
      )
      .map((result) => result.value);

    return successfulResults.flatMap(({ data: apiHotels = [] }) =>
      extractHotelsWithPrice(apiHotels),
    );
  };

  useEffect(() => {
    if (!firstListHotelCheckPrice?.length || !params) return;

    const fetchTripla = async () => {
      try {
        const { hotel_codes, ...rest } = params;
        const batches = chunkArray(hotel_codes, 5);
        const batchPromises = batches.map((batch) =>
          getLowestPriceHotels({
            ...rest,
            hotel_codes: batch,
          }),
        );
        const results = await Promise.allSettled(batchPromises);

        const allHotelsWithPrice = processBatchResults(results);

        setHotelsWithPrice(allHotelsWithPrice);
      } catch (err) {
        console.error("Unexpected error:", err);
      }
    };

    fetchTripla();
  }, [params, firstListHotelCheckPrice]);
  /**
   * Find brand data for banner display based on current brand
   */
  const brandData = brandBannerList?.find(
    (item) =>
      item.itemName &&
      (item.itemName === currentBrand?.item_name ||
        BrandFormatByItemName[item.itemName] === currentBrand?.item_name),
  );
  let hotelCount = japanAreas.flatMap((area) => area.hotels).length;

  /**
   * Calculate hotel count for display (filtered or total)
   */
  if (filterState?.hotelOrCity?.value || isMapView || !!currentTag) {
    hotelCount = resultLength;
  }
  /**
   * Hydration state for SSR/CSR rendering
   */
  let resultText: string | null = null;
  /**
   * Count of items for display (null if none found)
   */
  const hasHydrated = useHasHydrated();
  /**
   * Fast count for first-render: reads IndexDB (warm cache) or fires a lightweight
   * GraphQL query (cold cache / first visit). No-op for non-hotel-list pages.
   * Once the main flow resolves, resultText replaces this value in the display.
   */
  const { cachedCount, isLoadingCachedCount } = useHotelCount(isHotelListPage);
  const countItem = resultLength || null;

  // resultText does not need hasHydrated — hotelCount and countItem are plain
  // synchronous values derived from the Zustand store (already restored from
  // IndexDB before the first render on warm cache visits).
  if (isHotelListPage) {
    resultText = hotelCount ? `${hotelCount} 件が見つかりました` : null;
  } else {
    resultText = countItem ? `${countItem} 件が見つかりました` : null;
  }

  return (
    <div className="w-full items-center justify-items-center gap-16 overflow-x-hidden pb-20 font-[family-name:var(--font-geist-sans)]">
      {isMapView ? (
        <div className="h-full w-full">
          <HotelListMap
            visibleHotels={visibleHotels}
            setVisibleHotels={setVisibleHotels}
            filterState={filterState}
            setFilteredSections={setFilteredSections}
            hotelSliderSections={hotelSliderSections}
            currentBrand={currentBrand}
            brandIcons={brandIcons}
            defaultCity={defaultCity}
            defaultTokyo={defaultTokyo}
            isFirstCityChange={isFirstCityChange}
          />
        </div>
      ) : (
        <BannerSection
          bannerHotelList={bannerHotelList}
          brandData={brandData}
          currentBrand={currentBrand}
        />
      )}
      <div className="relative w-full px-4">
        <div
          className={`mb-5 flex flex-col items-center justify-between md:flex-row md:gap-2`}
        >
          <div
            className={`w-full ${isMapView ? "md:max-w-[300px]" : "md:max-w-[500px]"} `}
          >
            <SearchByCityAndHotel
              listOption={listHotelAndCityData}
              filterState={filterState}
              onFilterChange={onFilterChange}
              defaultvalue={defaultValue}
              loading={loading || loadingAllLocaleHotels}
              currentCity={currentCity}
              isShowClearAble={hasSlugCity}
              isFirstCityChange={isFirstCityChange}
            />
            <div className="mt-4 ml-0 w-full text-left text-[var(--color-gray-700)] md:ml-5">
              {resultText ? (
                // Main store loaded — show final count
                <>{resultText}</>
              ) : cachedCount ? (
                // Count from IndexDB/REST query ready — show immediately, independent of loading
                <>{`${cachedCount} 件が見つかりました`}</>
              ) : (isLoadingCachedCount || loading || loadingAllLocaleHotels) ? (
                // Count query or main store still in-flight — show skeleton
                <Skeleton className="h-[24px] w-[170px]" />
              ) : null}
            </div>
          </div>
          {isMapView ? (
            <FilterSection
              listOption={listHotelAndCityData}
              benefitList={benefitList}
              brandList={brandList}
              onFilterChange={onFilterChange}
              submitted={submitted}
              SetSubmitClicked={setSubmitted}
              filterState={filterState}
              defaultValue={defaultValue}
              currentBrand={currentBrand}
              isMapView={isMapView}
              setIsMapView={setIsMapView}
              isShowFilterBannerPage={brandData?.isShowFilter}
              isFirstCityChange={isFirstCityChange}
              isHotelListPage={isHotelListPage}
              isHotelOnSenPage={isHotelOnSenPage}
              currentTag={currentTag}
              hotelSliderSections={hotelSliderSections}
            />
          ) : (
            <PopularSection
              listOption={listHotelAndCityData}
              benefitList={benefitList}
              brandList={brandList}
              onFilterChange={onFilterChange}
              SetSubmitClicked={setSubmitted}
              filterState={filterState}
              defaultValue={defaultValue}
              currentBrand={currentBrand}
              isMapView={isMapView}
              setIsMapView={setIsMapView}
              isShowFilterBannerPage={brandData?.isShowFilter}
              isFirstCityChange={isFirstCityChange}
              isHotelListPage={isHotelListPage}
              isHotelOnSenPage={isHotelOnSenPage}
              currentTag={currentTag}
              hotelSliderSections={hotelSliderSections}
            />
          )}
        </div>
        {hasHydrated ? (
          <>
            {filteredSections.map((section, index) => {
              if (section?.data?.length > 0) {
                return (
                  <HotelListSlider
                    key={`hotel-slider-${index}-${section.data[0].documentId}`}
                    data={section.data}
                    amenitiesAndServiceData={section.amenitiesAndServiceData}
                    sectionTitle={section.sectionTitle}
                    revealEdge={section.revealEdge}
                    itemPerSlide={section.itemPerSlide}
                    sliderId={`hotel-slider-${index}`}
                    filterState={filterState}
                    currentBrand={currentBrand}
                    typeGroup={section.typeGroup}
                    hotelsWithPrice={hotelsWithPrice}
                    brandBannerList={brandBannerList}
                    isHotelListPage={isHotelListPage}
                    isFirstCityChange={isFirstCityChange}
                    isHotelOnSenPage={isHotelOnSenPage}
                    currentTag={currentTag}
                    priority={index === 0}
                  />
                );
              }
            })}
            {hotSpringSection && !filterState?.hotelOrCity?.value && (
              <HotelListSlider
                key={`hotel-hotspring-${hotSpringSection.data[0].documentId}`}
                data={hotSpringSection.data}
                amenitiesAndServiceData={
                  hotSpringSection.amenitiesAndServiceData
                }
                sectionTitle={hotSpringSection.sectionTitle}
                revealEdge={hotSpringSection.revealEdge}
                itemPerSlide={hotSpringSection.itemPerSlide}
                sliderId={`hotel-hotspring`}
                filterState={filterState}
                currentBrand={currentBrand}
                typeGroup={hotSpringSection.typeGroup}
                hotelsWithPrice={hotelsWithPrice}
                brandBannerList={brandBannerList}
              />
            )}
          </>
        ) : (
          <>
            {Array.from({ length: 2 }).map((_, row) => {
              const hotelResultRowKey = `hotel-result-row-${row}`;
              return (
                <div key={hotelResultRowKey} className="mb-10">
                  <div className="flex justify-center px-4 sm:justify-start">
                    <Skeleton className="h-[50px] w-[200px]" />
                  </div>
                  <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, col) => {
                      const hotelResultColKey = `hotel-result-col-${col}`;
                      return (
                        <div
                          className={cn(
                            "flex h-full flex-col overflow-hidden rounded-lg",
                            "border border-[var(--stroke-gray-light)] bg-white shadow-md",
                            "transition-shadow hover:border-[var(--stroke-gray-normal)]",
                          )}
                          key={hotelResultColKey}
                        >
                          <Skeleton className="h-[200px] w-full !rounded-none" />
                          <div className="p-4">
                            <div className="mt-5 flex w-full justify-between">
                              <Skeleton className="h-[20px] w-[80px]" />
                              <Skeleton className="h-[20px] w-[160px]" />
                            </div>
                            <Skeleton className="mt-3 h-[30px] w-[70%]" />
                            <Skeleton className="mt-3 h-[15px] w-[40%]" />
                            <Skeleton className="mt-10 h-[40px] w-full" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      <MemberShipSection
        className="w-full px-0 md:px-8"
        membershipProps={membershipProps}
      />
    </div>
  );
};

export default HotelListResult;
